import { api } from './client';

/**
 * Painel do parceiro — `/api/v1/painel/*` (só leitura).
 *
 * Mesmo login do app: o parceiro é um usuário comum vinculado a uma
 * organização (CPO terceiro ou investidor). O backend devolve 404 em tudo com
 * a flag desligada e em qualquer org que não seja do usuário.
 *
 * Dinheiro chega como string decimal ("123.45"); kWh e % como número. Receita
 * de roaming (Tupi/Spott/movE) é sempre ESTIMADA (kWh × tarifa de operador) e o
 * app tem que dizer isso na tela.
 *
 * `anterior` (v2) = mesmos números na janela imediatamente anterior, de mesmo
 * tamanho, recortada pelo acesso da org. `null` = sem acesso naquela janela:
 * o app não mostra variação.
 */

export type OrgKind = 'cpo' | 'investor';
export type Semaforo = 'verde' | 'amarelo' | 'vermelho';
export type Corrente = 'AC' | 'DC';
export type Granularidade = 'hora' | 'dia' | 'mes';

export interface PainelOrg {
  id: string;
  name: string;
  kind: OrgKind;
  network_view: boolean;
  role: 'owner' | 'viewer';
}

export interface PainelMe {
  orgs: PainelOrg[];
  network_view: boolean;
}

export interface PainelPeriod {
  start: string;
  end: string;
  preset: string;
  label: string;
}

export interface OrgHeader {
  id: string;
  name: string;
  kind: OrgKind;
  acesso_desde: string | null;
  carregadores: number;
}

export interface Kpis {
  kwh: number;
  recargas: number;
  /** Só app Bow: o roaming não identifica o motorista. */
  clientes_unicos: number;
  kwh_medio: number;
  co2_evitado_kg: number;
  estacoes: number;
  receita_bow_brl: string;
  receita_roaming_brl: string;
  receita_total_brl: string;
  ticket_medio_brl: string;
  ocupacao_pct: number;
  uptime_pct: number | null;
  nps: { score: number | null; respostas: number };
}

export interface CorrenteRow {
  corrente: Corrente;
  carregadores: number;
  recargas: number;
  kwh: number;
}

export interface Overview extends Kpis {
  org: OrgHeader;
  period: PainelPeriod;
  anterior: Kpis | null;
  /** Acesso na janela anterior menor que na atual: não mostrar % (base parcial). */
  anterior_parcial: boolean;
  por_corrente: CorrenteRow[];
}

export interface Origem {
  origem: string;
  label: string;
  estimada: boolean;
}

export interface Series {
  period: PainelPeriod;
  granularidade: Granularidade;
  /** "2026-10-01" (dia/mês) ou "2026-10-01T14:00:00-03:00" (hora, em SP). */
  labels: string[];
  origens: Origem[];
  kwh: Record<string, number[]>;
  recargas: Record<string, number[]>;
  receita_brl: Record<string, string[]>;
  /** Totais da janela anterior, alinhados por índice com `labels`. */
  anterior: { kwh: number[]; recargas: number[]; receita_brl: string[] } | null;
  anterior_parcial: boolean;
}

export interface Heatmap {
  period: PainelPeriod;
  dias: string[];
  grid: { recargas: number; kwh: number }[][];
}

export interface ChargerRow {
  hub_station_id: string;
  carregador: string;
  estacao: string | null;
  recargas: number;
  kwh: number;
  receita_brl: string;
  kwh_medio: number;
  potencia_efetiva_kw: number;
  potencia_nominal_kw: number;
  ocupacao_pct: number;
  uptime_pct: number | null;
  fila: number;
  corrente: Corrente;
  clientes_unicos: number;
}

export interface Chargers {
  period: PainelPeriod;
  carregadores: ChargerRow[];
}

export interface SessionItem {
  inicio: string;
  fim: string | null;
  duracao_min: number | null;
  kwh: number;
  valor_brl: string;
  valor_estimado: boolean;
  origem: string;
  origem_label: string;
  carregador: string;
  conector: number | null;
  cliente: string | null;
  motivo_fim: 'cliente' | 'limite' | 'falha' | 'outro';
}

export interface Sessions {
  period: PainelPeriod;
  page: number;
  page_size: number;
  total: number;
  items: SessionItem[];
}

export interface FinanceiroTotal {
  origem: string;
  label: string;
  estimada: boolean;
  kwh: number;
  receita_brl: string;
}

export interface FinanceiroCarregador {
  hub_station_id: string;
  carregador: string;
  recargas: number;
  kwh: number;
  receita_brl: string;
  /** null = a org não tinha este carregador na janela anterior. */
  receita_anterior_brl: string | null;
}

export interface Financeiro {
  period: PainelPeriod;
  linhas: { mes: string; origem: string; label: string; estimada: boolean; kwh: number; receita_brl: string }[];
  totais: FinanceiroTotal[];
  receita_total_brl: string;
  kwh_total: number;
  recargas: number;
  ticket_medio_brl: string;
  por_carregador: FinanceiroCarregador[];
  anterior: {
    totais: FinanceiroTotal[];
    receita_total_brl: string;
    kwh_total: number;
    recargas: number;
    ticket_medio_brl: string;
  } | null;
  anterior_parcial: boolean;
}

export interface LiveConnector {
  numero: number;
  tipo: string;
  potencia_max_kw: number;
  estado: string | null;
  semaforo: Semaforo;
  potencia_kw: number | null;
  soc_pct: number | null;
}

export interface LiveCharger {
  hub_station_id: string;
  nome: string;
  estado: string | null;
  semaforo: Semaforo;
  ultimo_contato: string | null;
  conectores: LiveConnector[];
  corrente: Corrente;
  potencia_nominal_kw: number;
  /** Slots OCPP que o firmware expõe (1 = os cabos compartilham). */
  plugs: number | null;
  /** Tarifa vigente do app Bow. */
  preco_kwh_brl: string | null;
  /** App Bow + roaming, desde o início do acesso da org. */
  kwh_acumulado: number;
}

export interface LiveStation {
  id: string;
  nome: string;
  endereco: string;
  latitude: number | null;
  longitude: number | null;
  carregadores: LiveCharger[];
}

export interface LiveResumo {
  carregadores: number;
  /** null = central fora: não dá pra afirmar quantos estão conectados. */
  conectados: number | null;
  por_semaforo: { semaforo: Semaforo; ac: number; dc: number }[];
}

export interface StationsLive {
  atualizado_em: string;
  hub_disponivel: boolean;
  resumo: LiveResumo;
  estacoes: LiveStation[];
}

export interface RedeMes {
  mes: string;
  kwh: number;
  receita_brl: string;
  recargas: number;
  motoristas_ativos: number;
  estacoes: number;
  ocupacao_pct: number;
  var_kwh_pct: number | null;
  var_receita_pct: number | null;
}

/** Período: os mesmos parâmetros dos dashboards do admin. */
export interface PeriodQuery {
  preset?: string;
  date_from?: string;
  date_to?: string;
}

export function periodQs(p: PeriodQuery, extra: Record<string, string | number> = {}): string {
  const qs = new URLSearchParams();
  if (p.date_from && p.date_to) {
    qs.set('date_from', p.date_from);
    qs.set('date_to', p.date_to);
  } else if (p.preset) {
    qs.set('preset', p.preset);
  }
  for (const [k, v] of Object.entries(extra)) qs.set(k, String(v));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

const org = (id: string) => `/painel/${encodeURIComponent(id)}`;

export const painelApi = {
  me: () => api.get<PainelMe>('/painel/me'),
  overview: (orgId: string, p: PeriodQuery) => api.get<Overview>(`${org(orgId)}/overview${periodQs(p)}`),
  series: (orgId: string, p: PeriodQuery, granularidade: Granularidade) =>
    api.get<Series>(`${org(orgId)}/series${periodQs(p, { granularidade })}`),
  heatmap: (orgId: string, p: PeriodQuery) => api.get<Heatmap>(`${org(orgId)}/heatmap${periodQs(p)}`),
  chargers: (orgId: string, p: PeriodQuery) => api.get<Chargers>(`${org(orgId)}/chargers${periodQs(p)}`),
  sessions: (orgId: string, p: PeriodQuery, page: number, pageSize = 25) =>
    api.get<Sessions>(`${org(orgId)}/sessions${periodQs(p, { page, page_size: pageSize })}`),
  financeiro: (orgId: string, p: PeriodQuery) => api.get<Financeiro>(`${org(orgId)}/financeiro${periodQs(p)}`),
  stationsLive: (orgId: string) => api.get<StationsLive>(`${org(orgId)}/stations/live`),
  rede: () => api.get<{ meses: RedeMes[] }>('/painel/rede/overview')
};

/** Cores fixas por origem — as mesmas da Consolidação do admin. Seguem a
 *  ENTIDADE, nunca a posição: filtrar o período não repinta ninguém. */
export const ORIGIN_COLORS: Record<string, string> = {
  bow: '#0090E2',
  tupi: '#00C072',
  spott: '#835DD0',
  move: '#DE7C00'
};
export const ORIGIN_FALLBACK = '#8C92B5';

export function originColor(origem: string): string {
  return ORIGIN_COLORS[origem] ?? ORIGIN_FALLBACK;
}

/** AC/DC — par validado (CVD ΔE 12, normal ΔE 30) e fora das cores de origem,
 *  pra rosca AC/DC não ser lida como "Bow" ou "Tupi". O azul-claro tem contraste
 *  baixo com o branco: a rosca leva rótulo e valor ao lado, sempre. */
export const CORRENTE_COLORS: Record<Corrente, string> = {
  AC: '#00BBDE',
  DC: '#C04CAE'
};

/** Comparativo por carregador: barra = período atual, traço de tinta = anterior. */
export const COMPARE_COLOR = '#8345BA';

export const MOTIVO_LABEL: Record<SessionItem['motivo_fim'], string> = {
  cliente: 'Encerrada pelo cliente',
  limite: 'Limite atingido',
  falha: 'Falha',
  outro: 'Outro'
};

/** Presets do seletor — os da Tupi: janelas móveis de 7/30/60/90 dias (`<n>d`
 *  no backend) + intervalo livre ("Personalizado"). */
export const PERIOD_PRESETS: { key: string; label: string }[] = [
  { key: '7d', label: '7 dias' },
  { key: '30d', label: '30 dias' },
  { key: '60d', label: '60 dias' },
  { key: '90d', label: '90 dias' }
];
export const DEFAULT_PRESET = '30d';

/** Série por hora só até 7 dias (o backend recusa acima disso com 422). */
export const MAX_HORA_MS = (7 * 24 + 1) * 3600_000;

/**
 * Decide pelo período PEDIDO (não pela resposta): ao trocar de 7 pra 30 dias, a
 * resposta antiga ainda está na tela e pediria "hora" pro período novo.
 * Intervalo livre: `date_to` é inclusivo no minuto (o backend soma 1 min).
 */
export function horaPermitida(p: PeriodQuery): boolean {
  if (p.date_from && p.date_to) {
    const ms = new Date(p.date_to).getTime() + 60_000 - new Date(p.date_from).getTime();
    return Number.isFinite(ms) && ms > 0 && ms <= MAX_HORA_MS;
  }
  const m = /^(\d+)d$/.exec(p.preset ?? DEFAULT_PRESET);
  return !!m && Number(m[1]) <= 7;
}
